import dayjs, { Dayjs } from 'dayjs';
import { DRIVER_ACTIVITY_STATUS_OPTIONS, DriverActivityAdminStatus } from '../graphql/types';
import { DATE_FORMAT } from './activityFormUtils';

export const LIST_PATH = '/partner-control/driver-activity';
export const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export interface ListFilters {
    name?: string;
    status?: DriverActivityAdminStatus;
    dateRange?: [Dayjs | null, Dayjs | null] | null;
}

export interface ListQuery {
    filters: ListFilters;
    page: number;
    pageSize: number;
}

/** 列表跳详情 / 编辑 / 新建时塞进 location.state，返回时用它拼回原筛选 */
export interface ListReturnState {
    listSearch?: string;
}

/** 严格解析 yyyy-MM-dd：格式不对或 2026-02-31 这种溢出日期一律丢弃 */
function parseDate(raw: string | null): Dayjs | null {
    if (!raw || !DATE_PATTERN.test(raw)) return null;
    const date = dayjs(raw);
    return date.isValid() && date.format(DATE_FORMAT) === raw ? date : null;
}

function parsePositiveInt(raw: string | null): number | null {
    if (!raw || !/^\d+$/.test(raw)) return null;
    const value = Number(raw);
    return Number.isSafeInteger(value) && value > 0 ? value : null;
}

/** 地址栏参数 → 列表状态；手改出来的非法值回落默认，不报错 */
export function parseListQuery(params: URLSearchParams): ListQuery {
    const name = params.get('name') ?? undefined;
    const rawStatus = params.get('status');
    const status = DRIVER_ACTIVITY_STATUS_OPTIONS.find((s) => s === rawStatus);
    const start = parseDate(params.get('startDate'));
    const end = parseDate(params.get('endDate'));
    const size = parsePositiveInt(params.get('size'));
    return {
        filters: {
            name: name || undefined,
            status,
            dateRange: start || end ? [start, end] : undefined,
        },
        page: parsePositiveInt(params.get('page')) ?? 1,
        pageSize: size !== null && PAGE_SIZE_OPTIONS.includes(size) ? size : DEFAULT_PAGE_SIZE,
    };
}

/** 列表状态 → 地址栏参数；默认值不写，无筛选时地址栏保持干净 */
export function buildListQuery({ filters, page, pageSize }: ListQuery): URLSearchParams {
    const params = new URLSearchParams();
    if (filters.name) params.set('name', filters.name);
    if (filters.status) params.set('status', filters.status);
    const [start, end] = filters.dateRange ?? [null, null];
    if (start) params.set('startDate', start.format(DATE_FORMAT));
    if (end) params.set('endDate', end.format(DATE_FORMAT));
    if (page > 1) params.set('page', String(page));
    if (pageSize !== DEFAULT_PAGE_SIZE) params.set('size', String(pageSize));
    return params;
}

/** 返回列表的地址：带着进来时的筛选；直接打开详情 / 编辑页（没有 state）就回干净的列表 */
export function listPathFromState(state: unknown): string {
    const search = (state as ListReturnState | null)?.listSearch;
    return typeof search === 'string' && search.startsWith('?') ? `${LIST_PATH}${search}` : LIST_PATH;
}
